import React from 'react';
import axios from 'axios';
import {
  Card,
  CardContent,
  CardMedia,
  Grid,
  LinearProgress, 
  Typography, 
  IconButton,
  Box,
  Button
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import ReactMarkdown from 'react-markdown';
import { markdownStyles } from './styles';

const BookCard = ({ book, onGenerateClick }) => {
  const gutenbergLink = `https://www.gutenberg.org/ebooks/${book.metadata.book_id}`;
  const coverImageUrl = `https://www.gutenberg.org/cache/epub/${book.metadata.book_id}/pg${book.metadata.book_id}.cover.medium.jpg`;

  const handleGenerateClick = async () => {
    try {
      const response = await axios.get('https://rgjkrflnxopeixwpsjae.supabase.co/functions/v1/graph', {
        params: {
          book_id: book.metadata.book_id,
          match_threshold: 0.75,
          top_n: 3
        },
        headers: {
            'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InJnamtyZmxueG9wZWl4d3BzamFlIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MTc4MDg1MzAsImV4cCI6MjAzMzM4NDUzMH0.d40jU9GnAyyRugrgmKZeS9aTfAi1_tBCeHzOulyfMAM', 
            'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InJnamtyZmxueG9wZWl4d3BzamFlIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MTc4MDg1MzAsImV4cCI6MjAzMzM4NDUzMH0.d40jU9GnAyyRugrgmKZeS9aTfAi1_tBCeHzOulyfMAM',
            'Content-Type': 'application/json',
          },
      });

      // const graphData = {
      //   nodes: response.data.nodes.map(node => ({
      //     id: node.id,
      //     group: node.group

      //   })),
      //   links: response.data.links.map(link => ({
      //     source: link.source,
      //     target: link.target,
      //     value: link.value
      //   }))
      // };

      const graphData = {
        nodes: response.data.nodes,
        links: response.data.links.map(link => ({
          source: link.source,
          target: link.target,
          value: link.value
        }))
      };

      onGenerateClick(graphData);
    } catch (error) {
      console.error('Error generating graph:', error);
    }
  };

  return (
    <Card sx={{ marginBottom: 2, width: '100%' }}>
      <Grid container>
        <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Box sx={{ width: '50%', aspectRatio: '3/4', position: 'relative' }}>
            <CardMedia
              component="img"
              image={coverImageUrl}
              alt={book.metadata.title}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = `https://www.gutenberg.org/cache/epub/${book.metadata.book_id}/pg${book.metadata.book_id}.cover.small.jpg`;
                e.target.width = 150;
              }}
              sx={{ height: '100%', width: '100%', objectFit: 'cover', position: 'absolute' }}
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" component="div" sx={{ 
                fontFamily: '"Times New Roman"', flexGrow: 1, textAlign: 'left' }}>
                {book.metadata.title}
              </Typography>
            </Box>
            <Typography variant="body1" color="text.secondary" gutterBottom sx={{ 
              fontFamily: '"Verdana"', fontStyle: 'italic', textAlign: 'left' }}>
              {book.metadata.author}
            </Typography>
            <IconButton aria-label="link to project gutenberg" href={gutenbergLink} target="_blank" rel="noopener noreferrer">
              <LinkIcon />
            </IconButton>
            <Button 
              variant="outlined" 
              endIcon={<KeyboardDoubleArrowRightIcon />}
              onClick={handleGenerateClick}
              sx={{ 
                mt: 1, 
                mb: 2,
                backgroundColor: 'white',
                color: 'black',
                borderColor: 'black',
                '&:hover': {
                  backgroundColor: '#f0f0f0',
                  borderColor: 'black',
                },
                fontFamily: 'Verdana, sans-serif',
                textTransform: 'none',
              }}
            >
              Generate Graph
            </Button>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
              <Box sx={{ width: '70%', mr: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Similarity %
                </Typography>
              </Box>
              <Box sx={{ width: '100%', mr: 1 }}>
                <LinearProgress variant="determinate" value={book.similarity * 100} />
              </Box>
              <Box sx={{ minWidth: 35, padding: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {(book.similarity * 100).toFixed(2)}%
                </Typography>
              </Box>
            </Box>
            <Box sx={{ mt: 2, mb: 2, maxHeight: '200px', width: '98%', overflow: 'auto',
              border: '1px solid #dfe1e5', boxShadow: '0 1px 6px rgba(32,33,36,.28)', padding: '5px'
             }}>
              <ReactMarkdown components={{
                p: ({ children }) => <Typography variant="body2" sx={markdownStyles}>{children}</Typography>,
                h1: ({ children }) => <Typography variant="h6" sx={markdownStyles}>{children}</Typography>,
                h2: ({ children }) => <Typography variant="subtitle1" sx={markdownStyles}>{children}</Typography>,
                h3: ({ children }) => <Typography variant="subtitle2" sx={markdownStyles}>{children}</Typography>,
                ul: ({ children }) => <ul style={{ ...markdownStyles, paddingLeft: '20px' }}>{children}</ul>,
                ol: ({ children }) => <ol style={{ ...markdownStyles, paddingLeft: '20px' }}>{children}</ol>,
              }}>
                {book.metadata.summary}
              </ReactMarkdown>
            </Box>
          </CardContent>
        </Grid>
      </Grid>
    </Card>
  );
};

export default BookCard;